import { FC, MouseEvent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import {
  ChevronDownSpriteIcon,
  GalleryImgSpriteIcon,
} from '@bridebook/web/app-basic/components/search/search-svg-sprite';
import { useSearchCarousel } from './use-search-carousel';
import { buildImageSrc, OPTIMIZED_IMAGE_QUALITY } from '@bridebook/toolbox/src/buildImageUrl';

const IMAGE_WIDTH = 400;
const IMAGE_HEIGHT = 286;
const MAX_IMAGES = 5;

const GALLERY_ICON_WIDTH = 42;
const GALLERY_ICON_HEIGHT = Math.round((GALLERY_ICON_WIDTH * 21) / 19);

const BULLET_WIDTH = 8;
const BULLET_SPACING = 4;
const SIDE_BULLETS = 1;

const getDotsLayout = (count: number, currentIndex: number) => {
  const fullBulletWidth = BULLET_WIDTH + BULLET_SPACING;
  const maxBullets = SIDE_BULLETS * 2 + 1;
  const visibleBullets = Math.min(count, maxBullets);
  const sideBullets = count < maxBullets ? (count - 1) / 2 : SIDE_BULLETS;
  const isFirstSlides = currentIndex < SIDE_BULLETS;
  const isLastSlides = currentIndex > count - SIDE_BULLETS - 1;

  const translateX = (() => {
    const base = -currentIndex * fullBulletWidth;
    if (isFirstSlides) return base - fullBulletWidth * (sideBullets - currentIndex);
    if (isLastSlides) return base + fullBulletWidth * (sideBullets - count + currentIndex + 1);
    return base;
  })();

  const getOpacity = (index: number): number => {
    if (index === currentIndex) return 1;
    if (currentIndex - sideBullets <= index && index < currentIndex) return 0.5;
    if (currentIndex < index && index <= currentIndex + sideBullets) return 0.5;
    if (isFirstSlides && index < visibleBullets) return 0.5;
    if (isLastSlides && index > count - visibleBullets - 1) return 0.5;
    return 0;
  };

  const wrapperWidth = visibleBullets * fullBulletWidth + 10;
  const bgWidth = count * fullBulletWidth + 2 * BULLET_SPACING;

  return { translateX, getOpacity, wrapperWidth, bgWidth };
};

interface SearchCarouselProps {
  photos: string[];
  isVenue: boolean;
  maxImages?: number;
  itemIndex?: number;
}

const SearchCarouselComponent: FC<SearchCarouselProps> = ({
  photos,
  isVenue,
  maxImages = MAX_IMAGES,
  itemIndex,
}) => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const images = photos.slice(0, maxImages);
  const gravity = isVenue ? 'auto' : 'face';
  const { emblaRef, scrollPrev, scrollNext, selectedIndex } = useSearchCarousel();

  const handlePrev = (e: MouseEvent) => {
    e.preventDefault();
    scrollPrev();
  };

  const handleNext = (e: MouseEvent) => {
    e.preventDefault();
    scrollNext();
  };

  const { translateX, getOpacity, wrapperWidth, bgWidth } = getDotsLayout(
    images.length,
    selectedIndex,
  );

  return (
    <div className="bso">
      <div ref={emblaRef}>
        <div className="bsh">
          {images.map((src, i) => (
            <div key={i}>
              <img
                src={buildImageSrc(src, {
                  w: IMAGE_WIDTH,
                  h: IMAGE_HEIGHT,
                  fit: 'cover',
                  gravity,
                  quality: OPTIMIZED_IMAGE_QUALITY,
                })}
                alt=""
                draggable={false}
                loading={i === 0 ? 'eager' : 'lazy'}
                // @ts-expect-error React does not recognize fetchpriority property
                fetchpriority={itemIndex === 0 && i === 0 ? 'high' : undefined}
              />
              {mounted && i === images.length - 1 && (
                <div className="bsr">
                  <GalleryImgSpriteIcon width={GALLERY_ICON_WIDTH} height={GALLERY_ICON_HEIGHT} />
                  <div>
                    <span>{t('supplierCard.seeProfile')}</span>
                    <ChevronDownSpriteIcon width={10} height={10} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {mounted && images.length > 1 && (
        <>
          <button onClick={handlePrev} className="bsu">
            <ChevronDownSpriteIcon width={13} height={13} />
          </button>
          <button onClick={handleNext} className="bsu">
            <ChevronDownSpriteIcon width={13} height={13} />
          </button>

          <div
            className="bsv"
            style={{ left: `calc(50% - ${wrapperWidth / 2}px)`, width: wrapperWidth }}>
            <div style={{ width: bgWidth }} />
            <div style={{ transform: `translateX(${translateX}px)` }}>
              {images.map((_, index) => (
                <div
                  key={index}
                  style={{
                    transform: `translateX(${index * (BULLET_WIDTH + BULLET_SPACING)}px)`,
                    opacity: getOpacity(index),
                  }}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const SearchCarousel = memo(SearchCarouselComponent);
